import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api, API_BASE, getCookie} from '../../api';

const FILE_TYPES = {
  'application/pdf': {color: 'text-red-600', bg: 'bg-red-50', label: 'PDF'},
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {color: 'text-blue-600', bg: 'bg-blue-50', label: 'DOCX'},
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {color: 'text-green-600', bg: 'bg-green-50', label: 'XLSX'},
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {color: 'text-orange-600', bg: 'bg-orange-50', label: 'PPTX'},
};

function FileTypeBadge({contentType, fileName}) {
  const match = FILE_TYPES[contentType];
  const ext = fileName?.split('.').pop()?.toUpperCase() || '?';
  const color = match?.color || 'text-gray-600';
  const bg = match?.bg || 'bg-gray-50';
  const label = match?.label || ext;
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded ${bg} ${color} text-xs font-medium`}>
      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
      {label}
    </span>
  );
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function Documents() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFolder = searchParams.get('folder') || null;
  const setCurrentFolder = (folderId) => {
    if (folderId) setSearchParams({folder: folderId});
    else setSearchParams({});
  };
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [folderModal, setFolderModal] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [createType, setCreateType] = useState(null);
  const [createName, setCreateName] = useState('');
  const [renamingDoc, setRenamingDoc] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const docInputRef = useRef();
  const dragCounter = useRef(0);

  useEffect(() => {
    loadDocuments();
    loadFolders();
  }, []);

  const loadDocuments = () => {
    api('/policies/documents').then(r => r.ok ? r.json() : []).then(setDocuments);
  };

  const loadFolders = () => {
    api('/policies/folders').then(r => r.ok ? r.json() : []).then(setFolders);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (currentFolder) formData.append('folderId', currentFolder);
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    try {
      await fetch(`${API_BASE}/policies/documents`, { method: 'POST', headers, body: formData });
      loadDocuments();
    } finally {
      setUploading(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDocUpload = (e) => uploadFile(e.target.files[0]);

  const handleDocDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    uploadFile(e.dataTransfer.files[0]);
  };

  const isEditable = (fileName) => /\.(docx|xlsx|pptx|doc|xls|ppt|odt|ods|odp)$/i.test(fileName);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    const res = await api('/policies/documents/create', {
      method: 'POST', body: JSON.stringify({ fileName: createName.trim(), type: createType, folderId: currentFolder })
    });
    setCreateType(null);
    if (res.ok) {
      const doc = await res.json();
      navigate(`/policies/documents/${doc.id}/edit`);
    }
  };

  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    const timer = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', close); };
  }, [openMenu]);

  const handleDocDelete = async () => {
    if (!deletingDoc) return;
    await api(`/policies/documents/${deletingDoc.id}`, { method: 'DELETE' });
    setDeletingDoc(null);
    loadDocuments();
  };

  const handleFolderSubmit = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) return;
    if (folderModal === 'new') {
      await api('/policies/folders', { method: 'POST', body: JSON.stringify({ name: folderName.trim() }) });
    } else {
      await api(`/policies/folders/${folderModal.id}`, { method: 'PUT', body: JSON.stringify({ name: folderName.trim() }) });
    }
    setFolderModal(null);
    loadFolders();
  };

  const handleDeleteFolder = async () => {
    if (!deletingFolder) return;
    await api(`/policies/folders/${deletingFolder.id}`, { method: 'DELETE' });
    setDeletingFolder(null);
    if (currentFolder === deletingFolder.id) setCurrentFolder(null);
    loadFolders();
    loadDocuments();
  };

  const handleTogglePublish = async (docId) => {
    await api(`/policies/documents/${docId}/publish`, { method: 'POST' });
    loadDocuments();
  };

  const handleMoveDoc = async (docId, folderId) => {
    await api(`/policies/documents/${docId}`, { method: 'PUT', body: JSON.stringify({ folderId }) });
    loadDocuments();
  };

  const handleDocDownload = async (docId) => {
    const res = await api(`/policies/documents/${docId}/url`);
    if (res.ok) {
      const data = await res.json();
      window.open(data.url, '_blank');
    }
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameValue.trim() || !renamingDoc) return;
    const res = await api(`/policies/documents/${renamingDoc.id}/rename`, {
      method: 'PUT', body: JSON.stringify({ fileName: renameValue.trim() })
    });
    setRenamingDoc(null);
    if (res.ok) loadDocuments();
  };

  const handlePreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewLoading(true);
    setPreviewUrl(null);
    const res = await api(`/policies/documents/${doc.id}/pdf`);
    if (res.ok) {
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    }
    setPreviewLoading(false);
  };

  const handleDownloadPdf = async (doc) => {
    const res = await api(`/policies/documents/${doc.id}/pdf`);
    if (res.ok) {
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const baseName = doc.fileName.includes('.') ? doc.fileName.substring(0, doc.fileName.lastIndexOf('.')) : doc.fileName;
      a.download = (doc.contentType === 'application/pdf' ? doc.fileName : baseName + '.pdf');
      a.click();
      URL.revokeObjectURL(a.href);
    }
  };

  return (
    <Layout>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Policy Documents</h2>

      <div className="bg-white rounded-lg shadow relative"
        onDragEnter={e => { e.preventDefault(); dragCounter.current++; setDragging(true); }}
        onDragOver={e => e.preventDefault()}
        onDragLeave={e => { e.preventDefault(); dragCounter.current--; if (dragCounter.current === 0) setDragging(false); }}
        onDrop={handleDocDrop}
      >
        {dragging && (
          <div className="absolute inset-0 bg-indigo-50 border-2 border-dashed border-indigo-400 rounded-lg z-10 flex items-center justify-center">
            <p className="text-indigo-600 text-lg font-medium">Drop files here to upload</p>
          </div>
        )}
        <div className="p-4 md:p-6 border-b flex justify-between items-center">
          <div>
            {currentFolder && (
              <nav className="flex items-center gap-1 mt-1 text-sm">
                <button onClick={() => setCurrentFolder(null)}
                  onDragOver={e => { e.preventDefault(); setDragOverFolder('root'); }}
                  onDragLeave={() => setDragOverFolder(null)}
                  onDrop={e => { e.preventDefault(); setDragOverFolder(null); const docId = e.dataTransfer.getData('text/plain'); if (docId) handleMoveDoc(docId, null); }}
                  className={`text-indigo-600 hover:text-indigo-800 px-1 rounded ${dragOverFolder === 'root' ? 'bg-indigo-100 ring-2 ring-indigo-400' : ''}`}>All Documents</button>
                <span className="text-gray-400">/</span>
                <span className="text-gray-700 font-medium">{folders.find(f => f.id === currentFolder)?.name}</span>
              </nav>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setFolderModal('new'); setFolderName(''); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">New Folder</button>
            <div className="relative">
              <button onClick={() => setShowNew(!showNew)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">New Document ▾</button>
              {showNew && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border z-20">
                  {[['docx','W','Document'],['xlsx','X','Spreadsheet'],['pptx','P','Presentation']].map(([type, icon, label]) => (
                    <button key={type} onClick={() => { setShowNew(false); setCreateType(type); setCreateName(''); }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
                      <span className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded flex items-center justify-center font-bold text-sm">{icon}</span>
                      <span className="text-gray-900">{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input type="file" ref={docInputRef} onChange={handleDocUpload} className="hidden" />
            <label onClick={() => docInputRef.current.click()}
              className={`px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
              {uploading ? 'Uploading...' : 'Upload'}
            </label>
          </div>
        </div>
        <div className={openMenu ? '' : 'overflow-x-auto'}>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visibility</th>
                <th className="px-6 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {!currentFolder && folders.map(f => (
                <tr key={`f-${f.id}`} className={`hover:bg-gray-50 ${dragOverFolder === f.id ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-400' : ''} ${openMenu === f.id ? 'relative z-50' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragOverFolder(f.id); }}
                  onDragLeave={() => setDragOverFolder(null)}
                  onDrop={e => { e.preventDefault(); setDragOverFolder(null); const docId = e.dataTransfer.getData('text/plain'); if (docId) handleMoveDoc(docId, f.id); }}>
                  <td className="px-6 py-4">
                    <button onClick={() => setCurrentFolder(f.id)} className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium">
                      <svg className="w-5 h-5 text-indigo-400" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                      {f.name}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-gray-400 hidden sm:table-cell">Folder</td>
                  <td className="px-6 py-4 hidden sm:table-cell">—</td>
                  <td className="px-6 py-4 text-gray-600">{f.createdAt ? new Date(f.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="px-6 py-4">—</td>
                  <td className="px-6 py-4">
                    <div className="relative">
                      <button onClick={() => setOpenMenu(openMenu === f.id ? null : f.id)} className="text-gray-400 hover:text-gray-600 p-1">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                      </button>
                      {openMenu === f.id && (
                        <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border z-50">
                          <button onClick={() => { setOpenMenu(null); setFolderModal({id: f.id, name: f.name}); setFolderName(f.name); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Rename</button>
                          <button onClick={() => { setOpenMenu(null); setDeletingFolder(f); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-red-600">Delete</button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {documents.filter(doc => currentFolder ? doc.folderId === currentFolder : !doc.folderId).map(doc => (
                <tr key={doc.id} className={`hover:bg-gray-50 ${openMenu === doc.id ? 'relative z-50' : ''}`} draggable
                  onDragStart={e => e.dataTransfer.setData('text/plain', doc.id)}>
                  <td className="px-6 py-4">
                    {isEditable(doc.fileName) ? (
                      <a href={`/policies/documents/${doc.id}/edit`} className="text-indigo-600 hover:text-indigo-800 font-medium">{doc.fileName}</a>
                    ) : (
                      <button onClick={() => handleDocDownload(doc.id)} className="text-indigo-600 hover:text-indigo-800 font-medium">{doc.fileName}</button>
                    )}
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell"><FileTypeBadge contentType={doc.contentType} fileName={doc.fileName} /></td>
                  <td className="px-6 py-4 text-gray-600 hidden sm:table-cell">{formatSize(doc.size)}</td>
                  <td className="px-6 py-4 text-gray-600">{doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="px-6 py-4">
                    <select value={doc.published ? 'published' : 'draft'}
                      onChange={e => { if ((e.target.value === 'published') !== doc.published) handleTogglePublish(doc.id); }}
                      className={`text-xs px-2 py-1 rounded border-0 cursor-pointer ${doc.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <div className="relative">
                      <button onClick={() => setOpenMenu(openMenu === doc.id ? null : doc.id)} className="text-gray-400 hover:text-gray-600 p-1">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                      </button>
                      {openMenu === doc.id && (
                        <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border z-50">
                          <button onClick={() => { setOpenMenu(null); handlePreview(doc); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Preview PDF</button>
                          <button onClick={() => { setOpenMenu(null); handleDownloadPdf(doc); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Download PDF</button>
                          <button onClick={() => { setOpenMenu(null); handleDocDownload(doc.id); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Download Original</button>
                          <button onClick={() => { setOpenMenu(null); setRenamingDoc(doc); setRenameValue(doc.fileName); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Rename</button>
                          <button onClick={() => { setOpenMenu(null); setDeletingDoc(doc); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-red-600">Delete</button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {folders.length === 0 && documents.length === 0 && <div className="text-center py-12 text-gray-500">No policy documents yet. Upload a file to get started.</div>}
        </div>
      </div>

      {folderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setFolderModal(null)}>
          <form onSubmit={handleFolderSubmit} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{folderModal === 'new' ? 'New Folder' : 'Rename Folder'}</h3>
            <input type="text" value={folderName} onChange={e => setFolderName(e.target.value)} autoFocus
              placeholder="Folder name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setFolderModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!folderName.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">{folderModal === 'new' ? 'Create' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {deletingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeletingDoc(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Document</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deletingDoc.fileName}"? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletingDoc(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDocDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {deletingFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeletingFolder(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Folder</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deletingFolder.name}"? Documents inside will be moved to the root.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletingFolder(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDeleteFolder} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {createType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setCreateType(null)}>
          <form onSubmit={handleCreate} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              New {createType === 'docx' ? 'Document' : createType === 'xlsx' ? 'Spreadsheet' : 'Presentation'}
            </h3>
            <input type="text" value={createName} onChange={e => setCreateName(e.target.value)} autoFocus
              placeholder="Enter name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setCreateType(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!createName.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">Create</button>
            </div>
          </form>
        </div>
      )}

      {renamingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setRenamingDoc(null)}>
          <form onSubmit={handleRename} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rename Document</h3>
            <input type="text" value={renameValue} onChange={e => setRenameValue(e.target.value)} autoFocus
              placeholder="File name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setRenamingDoc(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!renameValue.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">Rename</button>
            </div>
          </form>
        </div>
      )}

      {previewDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setPreviewDoc(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{previewDoc.fileName}</h3>
              <button onClick={() => { setPreviewDoc(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="flex-1 overflow-hidden">
              {previewLoading && <div className="flex items-center justify-center h-full text-gray-500">Converting to PDF...</div>}
              {previewUrl && <iframe src={previewUrl} className="w-full h-full border-0" title="PDF Preview" />}
              {!previewLoading && !previewUrl && <div className="flex items-center justify-center h-full text-red-500">Failed to load preview</div>}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
